import React, { useEffect, useMemo, useState } from 'react';
import { ArchiveIcon, BoxesIcon, CheckIcon, CircleDollarSignIcon, ClipboardListIcon, PackageCheckIcon, TrendingUpIcon } from 'lucide-react';
import { reconcileSubmission } from '../utils/submissionOutcome';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
import { UploadAIInsights } from './UploadAIInsights';
import { UnitsDashboard } from './UnitsDashboard';
type SummaryMetric = {
  label: string;
  value: string;
  detail: string;
};
type Batch = {
  arrived: string;
  name: string;
  received: number;
  msrp?: number;
  estimatedRecovery?: number;
  lineItems?: number;
};
const onePeopleSummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '189',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '83',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '80',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '1,902',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '1,024',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '1,019',
  detail: 'Units'
}];
const emptySummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '0',
  detail: 'Units'
}];
const onePeopleIncomingBatches = [{
  arrived: 'Feb 5, 2026',
  name: 'Spring overstock',
  expected: 1365,
  received: 1267,
  progress: 92.8
}, {
  arrived: 'Dec 6, 2025',
  name: 'Assembly part 4',
  expected: 7,
  received: 7,
  progress: 100
}, {
  arrived: 'Aug 30, 2025',
  name: 'August returns',
  expected: 337,
  received: 335,
  progress: 99.4
}, {
  arrived: 'Jul 10, 2025',
  name: 'July overstock',
  expected: 16,
  received: 16,
  progress: 100
}, {
  arrived: 'May 2, 2025',
  name: 'May overstock',
  expected: 258,
  received: 258,
  progress: 100
}];
const eddySummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '320',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '86',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '82',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '2,981',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '682',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '669',
  detail: 'Units'
}];
const eddyIncomingBatches = [{
  arrived: 'Nov 4, 2026',
  name: 'Eddy — January 3PL transfer',
  expected: 85,
  received: 86,
  progress: 100
}, {
  arrived: 'Jan 28, 2026',
  name: 'Eddy Samples',
  expected: 0,
  received: 0,
  progress: 0
}, {
  arrived: 'Dec 16, 2025',
  name: 'Eddy — December 2025 3PL transfer',
  expected: 1103,
  received: 1105,
  progress: 100
}, {
  arrived: 'Nov 21, 2025',
  name: 'Eddy 3PL transfer',
  expected: 118,
  received: 120,
  progress: 100
}, {
  arrived: 'Oct 10, 2025',
  name: 'Eddy 3PL Transfer',
  expected: 87,
  received: 86,
  progress: 100
}, {
  arrived: 'May 22, 2025',
  name: 'Eddy — TJX Overflow',
  expected: 0,
  received: 223,
  progress: 97.4
}, {
  arrived: 'May 19, 2025',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 845,
  progress: 99.7
}, {
  arrived: 'Sep 30, 2024',
  name: 'Eddy — October 3PL Transfer',
  expected: 22,
  received: 25,
  progress: 100
}, {
  arrived: 'Aug 13, 2024',
  name: 'Eddy — Fall 2024',
  expected: 168,
  received: 169,
  progress: 100
}, {
  arrived: 'Apr 8, 2024',
  name: 'Eddy April 2024',
  expected: 0,
  received: 0,
  progress: 100
}, {
  arrived: 'Jan 18, 2024',
  name: 'Eddy — Jan 2024',
  expected: 5,
  received: 0,
  progress: 100
}, {
  arrived: 'Dec 20, 1905',
  name: 'Eddy — 3PL transfer old',
  expected: 0,
  received: 183,
  progress: 100
}, {
  arrived: '—',
  name: 'Eddy — June TRX',
  expected: 139,
  received: 139,
  progress: 98.1
}, {
  arrived: '—',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 0,
  progress: 0
}];
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function InventoryDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const hasOnePeopleData = selectedOwner === '1 People';
  const hasEddyData = selectedOwner === 'Eddy';
  const isNisolo = selectedOwner === 'Nisolo';
  const {
    inventory: nisolo
  } = useNisoloProducts(isNisolo);
  const {
    data: nisoloSubmissions,
    loading: submissionsLoading,
    error: submissionsError
  } = useNisoloSubmissions(isNisolo);
  const {
    data: nisoloOrders
  } = useNisoloOrders(isNisolo);
  const incomingBatches: Batch[] = isNisolo ? nisoloSubmissions?.submissions ?? [] : hasOnePeopleData ? onePeopleIncomingBatches : hasEddyData ? eddyIncomingBatches : [];
  const [section, setSection] = useState<'Submissions' | 'Units'>('Submissions');
  const [deselected, setDeselected] = useState<string[]>([]);
  useEffect(() => setDeselected([]), [selectedOwner]);
  const isSelected = (batch: Batch) => !deselected.includes(batch.name);
  const toggleBatch = (batch: Batch) => setDeselected(current => current.includes(batch.name) ? current.filter(name => name !== batch.name) : [...current, batch.name]);
  const allSelected = deselected.length === 0;
  const toggleAll = () => setDeselected(allSelected ? incomingBatches.map(batch => batch.name) : []);
  const selectedBatches = incomingBatches.filter(isSelected);
  const batchUnitTotal = incomingBatches.reduce((sum, batch) => sum + batch.received, 0);
  const selectedUnits = selectedBatches.reduce((sum, batch) => sum + batch.received, 0);
  const share = incomingBatches.length === 0 ? 1 : batchUnitTotal > 0 ? selectedUnits / batchUnitTotal : selectedBatches.length / incomingBatches.length;
  const base = isNisolo ? {
    receivedProducts: nisolo?.totalProducts ?? 0,
    availableProducts: nisolo?.products.filter(product => product.units > 0).length ?? 0,
    liveProducts: nisolo?.liveProducts ?? 0,
    receivedUnits: nisoloSubmissions?.totalReceived ?? 0,
    availableUnits: nisolo?.totalUnits ?? 0,
    liveUnits: nisolo?.liveUnits ?? 0,
    totalMsrp: nisoloSubmissions?.totalMsrp ?? 0,
    maxOpportunity: nisoloSubmissions?.totalEstimatedRecovery ?? 0,
    receivedValue: nisoloSubmissions?.totalMsrp ?? 0,
    soldUnits: nisoloOrders?.units ?? 0,
    totalSales: nisoloOrders?.sales ?? 0
  } : hasOnePeopleData ? {
    receivedProducts: 189,
    availableProducts: 83,
    liveProducts: 80,
    receivedUnits: 1902,
    availableUnits: 1024,
    liveUnits: 1019,
    totalMsrp: 130993,
    maxOpportunity: 65437,
    receivedValue: 130993,
    soldUnits: 2920,
    totalSales: 49411
  } : hasEddyData ? {
    receivedProducts: 320,
    availableProducts: 86,
    liveProducts: 82,
    receivedUnits: 2981,
    availableUnits: 682,
    liveUnits: 669,
    totalMsrp: 321656,
    maxOpportunity: 160828,
    receivedValue: 320582,
    soldUnits: 4010,
    totalSales: 325875
  } : {
    receivedProducts: 0,
    availableProducts: 0,
    liveProducts: 0,
    receivedUnits: 0,
    availableUnits: 0,
    liveUnits: 0,
    totalMsrp: 0,
    maxOpportunity: 0,
    receivedValue: 0,
    soldUnits: 0,
    totalSales: 0
  };
  const scaled = (value: number) => Math.round(value * share);
  const selectedLines = selectedBatches.reduce((sum, batch) => sum + (batch.lineItems ?? 0), 0);
  const receivedProducts = selectedLines > 0 ? selectedLines : scaled(base.receivedProducts);
  const receivedUnits = incomingBatches.length > 0 ? selectedUnits : base.receivedUnits;
  const predictedRecovery = isNisolo ? selectedBatches.reduce((sum, batch) => sum + (batch.estimatedRecovery ?? 0), 0) : scaled(base.maxOpportunity);

  // For Nisolo, sales are attributed to the selected submissions by barcode so
  // the summary always reconciles with each upload's report.
  const nisoloMatched = useMemo(() => {
    if (!isNisolo || !nisoloSubmissions) return {
      units: 0,
      sales: 0
    };
    return nisoloSubmissions.submissions.filter(submission => !deselected.includes(submission.name)).reduce((totals, submission) => {
      const outcome = reconcileSubmission(submission, nisoloOrders);
      return {
        units: totals.units + outcome.soldUnits,
        sales: totals.sales + outcome.actualSales
      };
    }, {
      units: 0,
      sales: 0
    });
  }, [isNisolo, nisoloSubmissions, nisoloOrders, deselected]);
  const soldUnits = isNisolo ? nisoloMatched.units : scaled(base.soldUnits);
  const totalSales = isNisolo ? nisoloMatched.sales : scaled(base.totalSales);
  const perUnit = (value: number, units: number) => units > 0 ? (value / units).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }) : '—';
  const summaryMetrics: SummaryMetric[] = [{
    label: 'Unit count',
    value: receivedUnits.toLocaleString('en-US'),
    detail: `Across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`
  }, {
    label: 'Predicted recovery',
    value: currency(predictedRecovery),
    detail: 'Carli estimate'
  }, {
    label: 'Predicted recovery / unit',
    value: perUnit(predictedRecovery, receivedUnits),
    detail: 'Per unit received'
  }, {
    label: 'Units sold',
    value: soldUnits.toLocaleString('en-US'),
    detail: isNisolo ? 'Matched to selected uploads' : 'All recovery channels'
  }, {
    label: 'Total sales',
    value: currency(totalSales),
    detail: 'Gross recovery revenue'
  }, {
    label: 'Actual recovery / unit',
    value: perUnit(totalSales, soldUnits),
    detail: soldUnits > 0 ? 'Per unit sold' : 'No sales yet'
  }];
  const selectedMsrp = selectedBatches.reduce((sum, batch) => sum + (batch.msrp ?? 0), 0);
  const msrpValue = selectedMsrp > 0 ? selectedMsrp : scaled(base.totalMsrp);
  const totalMsrp = currency(msrpValue);
  const maxOpportunity = currency(predictedRecovery);
  const totalReceivedValue = currency(msrpValue);
  const overstockDetail = `${receivedUnits.toLocaleString('en-US')} units across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`;
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="inventory-heading" data-id="element-1020" data-mp="1awyu9tetc1qr">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1021" data-mp="x5u3zhxc4lt3">
        <div data-id="element-1022" data-mp="115b9ucgobkys">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1023" data-mp="1152tubp97zjd">
            <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1024" data-mp="n97l2yokk4tp" />
            Inventory
          </span>
          <h1 id="inventory-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1025" data-mp="e66e6vrvq9oz">
            Inventory dashboard
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1026" data-mp="i8a1qirtp4qm">
            A clear view of inventory received, prepared for sale, and currently live across your approved recovery channels.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1027" data-mp="yfoonosdbbs">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1028" data-mp="3t39pueiyoaee" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <div className="mt-6 flex gap-1 rounded-full border border-ink/10 bg-cream/60 p-1 w-fit" role="tablist" aria-label="Inventory sections" data-id="element-1029" data-mp="aqbyux45crwd">
        {(['Submissions', 'Units'] as const).map(tab => <button key={tab} type="button" role="tab" aria-selected={section === tab} onClick={() => setSection(tab)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${section === tab ? 'bg-[#BB5E40] text-cream shadow-sm' : 'text-ink/55 hover:text-ink'}`} data-id="element-1030" data-mp="ko9krdyjaclt">
            {tab}
          </button>)}
      </div>

      {section === 'Units' ? <div className="mt-6" data-id="element-1031" data-mp="2qgopakybyykd">
          <UnitsDashboard selectedOwner={selectedOwner} embedded data-id="element-1032" data-mp="2qhhon0uymlsh" />
        </div> : <>
      <section className="mt-6 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" aria-label="Inventory overview" data-id="element-1033" data-mp="uecz5qreufl3">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="summary-heading" data-id="element-1034" data-mp="zd6bhfuyo0jo">
          <div className="flex items-center gap-2" data-id="element-1035" data-mp="18cjatfpdf8qo">
            <BoxesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1036" data-mp="18dcnsyexauh9" />
            <h2 id="summary-heading" className="text-lg font-semibold" data-id="element-1037" data-mp="1dabflab46pca">Summary</h2>
          </div>
          <div className="mt-6 grid gap-x-5 gap-y-6 sm:grid-cols-3" data-id="element-1038" data-mp="1i8we95apx0ym">
            {summaryMetrics.map((metric, index) => <div key={metric.label} className={`border-[#BB5E40]/25 pl-4 ${index % 3 === 0 ? 'border-l-0 pl-0' : 'border-l-2'} ${index >= 3 ? 'border-t pt-5 sm:border-t-0 sm:pt-0' : ''}`} data-id="element-1039" data-mp="1n30nxwbdbn3e" data-mpi={index}>
                <p className="text-sm text-ink/55" data-id="element-1040" data-mp="1w3fbl2bt1ea2">{metric.label}</p>
                <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1041" data-mp="211qlfd23nosa">{metric.value}</p>
                <p className="mt-1 text-xs text-ink/50" data-id="element-1042" data-mp="3ljj1bp2im0ck">{metric.detail}</p>
              </div>)}
          </div>
        </article>

        <article className="rounded-3xl border border-ink/10 bg-forest p-5 text-cream shadow-sm sm:p-7" aria-labelledby="opportunity-heading" data-id="element-1043" data-mp="3ci1l86p3xynv">
          <div className="flex items-center gap-2 text-cream/80" data-id="element-1044" data-mp="37k01rknrrxbv">
            <CircleDollarSignIcon className="h-4 w-4" aria-hidden="true" data-id="element-1045" data-mp="83lci7hfj0gd" />
            <h2 id="opportunity-heading" className="text-lg font-semibold text-cream" data-id="element-1046" data-mp="85xtahbgb4ru">Recovery value</h2>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5" data-id="element-1047" data-mp="3vgnn3nsg7tsu">
            <div data-id="element-1048" data-mp="3vfcglg7mc7y1">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-1049" data-mp="3vet3qdwts9qv">Total MSRP</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1050" data-mp="3qfzwv4c1ngzo">{totalMsrp}</p>
            </div>
            <div data-id="element-1051" data-mp="n1dowqroj8qc">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-1052" data-mp="n0u5t6qameaq">Max opportunity</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1053" data-mp="6kjo7efbh7dh">{maxOpportunity}</p>
            </div>
          </div>
          <p className="mt-5 border-t border-cream/15 pt-4 text-sm leading-relaxed text-cream/70" data-id="element-1054" data-mp="blz6wzu0v1sp">Based on inventory that has been received and evaluated for recovery.</p>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[0.78fr_1.22fr]" data-id="element-1055" data-mp="2m3ukroqd0r3">
        <article className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="overstock-heading" data-id="element-1056" data-mp="qavhp4ngqsx0">
          <div className="flex items-center gap-2" data-id="element-1057" data-mp="ud9xtgzqzekl">
            <PackageCheckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1058" data-mp="gkk9h5wqxxkb" />
            <h2 id="overstock-heading" className="text-base font-semibold" data-id="element-1059" data-mp="lic1dzxe5joi">Overstock</h2>
          </div>
          <p className="mt-5 text-sm text-ink/55" data-id="element-1060" data-mp="197wl4l1dckyu">Total MSRP value</p>
          <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-ink" data-id="element-1061" data-mp="196lhcl7xbd7q">{totalReceivedValue}</p>
          <p className="mt-1 text-sm text-ink/55" data-id="element-1062" data-mp="1ndq6tohxy59k">{overstockDetail}</p>
          <div className="mt-6 rounded-xl bg-[#BB5E40]/5 p-4" data-id="element-1063" data-mp="1nbvjk3j7612b">
            <div className="flex items-start gap-2" data-id="element-1064" data-mp="1jbbm0yupovfn">
              <TrendingUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1065" data-mp="1x3ruwgaannoh" />
              <p className="text-sm leading-relaxed text-ink/65" data-id="element-1066" data-mp="1s4onwqar77dv">Inventory moves into recovery once it is received, checked in, and matched to your current channel and pricing rules.</p>
            </div>
          </div>
        </article>

        <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="incoming-heading" data-id="element-1067" data-mp="10ek7t3u116ig">
          <div className="flex items-center gap-2 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-1068" data-mp="1eeuxw4niih2k">
            <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1069" data-mp="19cm6hcqafn2k" />
            <h2 id="incoming-heading" className="text-base font-semibold" data-id="element-1070" data-mp="2pr5kftbnxgyk">Inventory submissions</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-1071" data-mp="2ksuehtohnqlm">
            <table className="w-full min-w-[700px] text-left text-sm" data-id="element-1072" data-mp="2subp6znjrq0x">
              <thead className="bg-cream/65 text-xs text-ink/50" data-id="element-1073" data-mp="2sw4mhoc9sxw9">
                <tr data-id="element-1074" data-mp="2wuc2ui2tnx6x">
                  <th scope="col" className="py-3 pl-5 pr-2 font-semibold sm:pl-6" data-id="element-1075" data-mp="2wtu1x857suso">
                    <span className="flex items-center gap-2.5" data-id="element-1076" data-mp="31v9j2p49uh5s">
                      <SelectBox checked={allSelected} onChange={toggleAll} label="Select all submissions" data-id="element-1077" data-mp="36u302riroqap" />
                      Uploaded
                    </span>
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1078" data-mp="2e4mas2qg1pip">Batch</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1079" data-mp="2j48oeda6p7s1">Unit count</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1080" data-mp="2j2fij07earl3">MSRP value</th>
                  <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-1081" data-mp="2nws35dkly8yf">Estimated recovery</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1082" data-mp="1vdmiarjqpxko">
                {incomingBatches.length > 0 ? incomingBatches.map(batch => {
                  const checked = isSelected(batch);
                  return <tr key={batch.name} className={checked ? '' : 'opacity-45'} data-id="element-1083" data-mp="1iwtspsuz7c11">
                      <td className="whitespace-nowrap py-4 pl-5 pr-2 text-ink/60 sm:pl-6" data-id="element-1084" data-mp="1dvnwvm60r8na">
                        <span className="flex items-center gap-2.5" data-id="element-1085" data-mp="18wumi2m13xrc">
                          <SelectBox checked={checked} onChange={() => toggleBatch(batch)} label={`Include ${batch.name} in totals`} data-id="element-1086" data-mp="14w0yzaci0kyz" />
                          {batch.arrived}
                        </span>
                      </td>
                      <th scope="row" className="px-3 py-4 text-left font-medium text-ink" data-id="element-1087" data-mp="v79875j6c6pn">{batch.name}</th>
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1088" data-mp="q8xxytoyvu6w">{batch.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1089" data-mp="h6oom75fq6v3">{batch.msrp && batch.msrp > 0 ? currency(batch.msrp) : '—'}</td>
                      <td className="px-5 py-4 text-right tabular-nums text-ink/70 sm:px-6" data-id="element-1090" data-mp="254pjumjvaye5">{batch.estimatedRecovery && batch.estimatedRecovery > 0 ? currency(batch.estimatedRecovery) : '—'}</td>
                    </tr>;
                }) : <tr data-id="element-1091" data-mp="2041h53ma0t43">
                    <td colSpan={5} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1092" data-mp="203i7sbaozd1o">Select an owner to view inventory submissions.</td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {isNisolo && <section className="mt-5" aria-label="AI insights" data-id="element-1093" data-mp="1cfibywyiwwg8">
          <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter(submission => !deselected.includes(submission.name))} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} data-id="element-1094" data-mp="1hdtioytiuqu8" />
        </section>}
        </>}
    </div>;
}
function SelectBox({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={onChange} className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${checked ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/25 bg-paper hover:border-[#BB5E40]/50'}`} data-id="element-1095" data-mp="jtk4giq7yvgy">
      {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-1096" data-mp="12rmsau716md6" />}
    </button>;
}